import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2, FolderOpen, Camera } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Separator } from '@/shared/components/ui/separator';
import { Badge } from '@/shared/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { FloatingLabelDatePicker } from '@/shared/components/custom/form-field/FloatingLabelDatePicker';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/ColorPickerPopover';
import { ProjectAvatar } from '@/shared/components/custom/avatars/ProjectAvatar';
import { useUIStore } from '@/core/stores/ui.store';
import {
  useProject,
  useUpdateProject,
  useDeleteProject,
  useUploadProjectIcon,
  useDeleteProjectIcon,
  updateProjectInfoSchema,
  type UpdateProjectInfoInput,
} from '@/features/projects/public';
import { getColorValue } from '@/core/lib/colors';
import { ProjectInfoSkeleton } from './project/ProjectInfoSkeleton';
import { ProjectDeleteDialog } from './project/ProjectDeleteDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { FileUploader } from '@/shared/components/custom/file-uploader/FileUploader';
import { ImageCropDialog } from '@/shared/components/custom/image-crop/ImageCropDialog';
import { SettingsSectionHeader } from '../SettingsSectionHeader';
import { DangerZone } from '../DangerZone';

const DEFAULT_COLOR = 'indigo-violet';

export function ProjectInfoSection() {
  const { t } = useTranslation('settings');
  const { t: tProj } = useTranslation('projects');

  const selectedProjectId = useUIStore((s) => s.selectedProjectId);

  const { data: project, isLoading, isError } = useProject(selectedProjectId);
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const uploadIcon = useUploadProjectIcon();
  const deleteIcon = useDeleteProjectIcon();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const { toast } = useToast();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const form = useForm<UpdateProjectInfoInput>({
    resolver: zodResolver(updateProjectInfoSchema(tProj)),
    defaultValues: {
      name: '',
      description: '',
      color: DEFAULT_COLOR,
      startDate: '',
      endDate: '',
    },
  });

  const {
    formState: { isDirty, isSubmitting },
  } = form;

  const watchedName = form.watch('name');
  const watchedColor = form.watch('color') ?? DEFAULT_COLOR;

  useEffect(() => {
    if (!project) return;
    form.reset({
      name: project.name ?? '',
      description: project.description ?? '',
      color: project.color ?? DEFAULT_COLOR,
      startDate: project.startDate ?? '',
      endDate: project.endDate ?? '',
    });
  }, [project, form]);

  const onSubmit = form.handleSubmit(async (data) => {
    if (!selectedProjectId) return;
    try {
      await updateProject.mutateAsync({
        projectId: selectedProjectId,
        payload: {
          name: data.name,
          description: data.description || undefined,
          color: data.color || undefined,
          startDate: data.startDate || undefined,
          endDate: data.endDate || undefined,
        },
      });
      form.reset(data);
    } catch {
      // global handler shows error toast
    }
  });

  const openUploaderRef = useRef<(() => void) | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  const handleIconFilesAccepted = (_files: File[], previewUrls: string[]) => {
    if (previewUrls[0]) setCropImageSrc(previewUrls[0]);
  };

  const handleIconFilesRejected = () => {
    toast.error({ title: t('profile.avatar.typeError') });
  };

  const handleCropConfirm = (blob: Blob) => {
    setCropImageSrc(null);
    if (selectedProjectId) {
      uploadIcon.mutate({
        projectId: selectedProjectId,
        file: new File([blob], 'icon.jpg', { type: blob.type }),
      });
    }
  };

  const handleCropCancel = () => {
    setCropImageSrc(null);
  };

  const handleIconRemove = async () => {
    if (!selectedProjectId) return;
    const result = await confirm({
      title: t('projectInfo.avatarRemove') ?? t('profile.avatar.remove'),
      description: t('projectInfo.avatarRemove') ?? t('profile.avatar.remove'),
      variant: 'destructive',
    });
    if (result.confirmed) {
      deleteIcon.mutate(selectedProjectId);
    }
  };

  /* ── Sin proyecto seleccionado ───────────────────────────── */

  if (!selectedProjectId) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 py-16 text-center"
        data-testid="settings-project-no-selection"
      >
        <FolderOpen className="h-10 w-10 text-muted-foreground/50" />
        <p className="text-sm font-medium text-foreground">{t('projectInfo.noProjectTitle')}</p>
        <p className="text-sm text-muted-foreground">{t('projectInfo.noProjectDescription')}</p>
      </div>
    );
  }

  /* ── Estados de carga y error ────────────────────────────── */

  if (isLoading) return <ProjectInfoSkeleton />;

  if (isError) {
    return (
      <div data-testid="settings-project-info-error" className="text-sm text-destructive py-4">
        {t('projectInfo.loadError')}
      </div>
    );
  }

  /* ── Render ──────────────────────────────────────────────── */

  return (
    <div className="space-y-8" data-testid="settings-project-info-section">
      {/* ── 1. Datos del proyecto ─────────────────────────────── */}
      <div>
        <div className="pb-6">
          <SettingsSectionHeader>{t('projectInfo.dataTitle')}</SettingsSectionHeader>
          <p className="text-sm text-muted-foreground mt-1">{t('projectInfo.dataDescription')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-8 items-start">
            <div className="flex justify-center">
              <FileUploader
                accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] }}
                maxSize={2 * 1024 * 1024}
                onFilesAccepted={handleIconFilesAccepted}
                onFilesRejected={handleIconFilesRejected}
              >
                {({ getInputProps, open }) => {
                  openUploaderRef.current = open;
                  return (
                    <div className="relative" data-testid="settings-project-info-avatar">
                      <ProjectAvatar
                        name={watchedName || '?'}
                        color={watchedColor}
                        src={project?.icon ?? undefined}
                        size="2xl"
                        className="ring-2 ring-background"
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="absolute bottom-1 right-1 flex size-7 items-center justify-center rounded-full bg-foreground text-background shadow-md hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            data-testid="settings-project-info-avatar-edit"
                          >
                            <Camera className="size-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center">
                          <DropdownMenuItem
                            onClick={() => openUploaderRef.current?.()}
                            data-testid="settings-project-info-avatar-upload"
                          >
                            {t('projectInfo.avatarUpload')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={handleIconRemove}
                            disabled={!project?.icon}
                            data-testid="settings-project-info-avatar-remove"
                          >
                            {t('projectInfo.avatarRemove')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <input
                        {...getInputProps()}
                        data-testid="settings-project-info-avatar-input"
                      />
                    </div>
                  );
                }}
              </FileUploader>
              <ImageCropDialog
                open={!!cropImageSrc}
                imageSrc={cropImageSrc}
                cropShape="rect"
                onConfirm={handleCropConfirm}
                onCancel={handleCropCancel}
                labels={{
                  title: t('projectInfo.avatarCrop.title'),
                  zoom: t('projectInfo.avatarCrop.zoom'),
                  rotate: t('projectInfo.avatarCrop.rotate'),
                  confirm: t('projectInfo.avatarCrop.confirm'),
                  cancel: t('projectInfo.avatarCrop.cancel'),
                }}
              />
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">{t('projectInfo.colorLabel')}</p>
                <div className="flex items-center gap-3">
                  <Controller
                    control={form.control}
                    name="color"
                    render={({ field }) => (
                      <ColorPickerPopover
                        value={field.value ?? DEFAULT_COLOR}
                        onSelect={(token) => field.onChange(token)}
                        trigger={
                          <button
                            type="button"
                            data-testid="settings-project-info-color-trigger"
                            aria-label={t('projectInfo.colorLabel')}
                            className="size-8 rounded-full shrink-0 ring-2 ring-offset-2 ring-offset-background ring-border hover:ring-primary transition-all focus-visible:outline-none focus-visible:ring-primary"
                            style={{
                              background:
                                getColorValue(field.value ?? DEFAULT_COLOR) || field.value,
                            }}
                          />
                        }
                      />
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('projectInfo.colorDescription')}
                  </p>
                </div>
              </div>

              <FormFieldInput
                control={form.control}
                name="name"
                label={t('projectInfo.name')}
                floatingLabel
                data-testid="settings-project-info-name-input"
              />

              <FormFieldTextarea
                control={form.control}
                name="description"
                label={t('projectInfo.descriptionField')}
                floatingLabel
                rows={3}
                data-testid="settings-project-info-description-input"
              />

              <div className="grid grid-cols-2 gap-4">
                <Controller
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FloatingLabelDatePicker
                      id="startDate"
                      label={t('projectInfo.startDate')}
                      value={field.value || null}
                      onChange={(value) => field.onChange(value ?? '')}
                      data-testid="settings-project-info-start-date-input"
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FloatingLabelDatePicker
                      id="endDate"
                      label={t('projectInfo.endDate')}
                      value={field.value || null}
                      onChange={(value) => field.onChange(value ?? '')}
                      data-testid="settings-project-info-end-date-input"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!isDirty || isSubmitting}
              data-testid="settings-project-info-save-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('projectInfo.saving')}
                </>
              ) : (
                t('projectInfo.save')
              )}
            </Button>
          </div>
        </form>
      </div>

      <Separator />

      {/* ── 2. Estado del proyecto ────────────────────────────── */}
      <div data-testid="settings-project-info-status-section">
        <div className="pb-6">
          <SettingsSectionHeader>{t('projectInfo.statusTitle')}</SettingsSectionHeader>
          <p className="text-sm text-muted-foreground mt-1">{t('projectInfo.statusDescription')}</p>
        </div>

        <dl className="space-y-4">
          <div className="flex items-center justify-between">
            <dt className="text-sm text-muted-foreground">{t('projectInfo.statusLabel')}</dt>
            <dd data-testid="settings-project-info-status">
              <Badge variant={project?.isArchived ? 'secondary' : 'default'}>
                {project?.isArchived
                  ? tProj('status.archived')
                  : (project?.status ?? tProj('status.active'))}
              </Badge>
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm text-muted-foreground">{t('projectInfo.archivedLabel')}</dt>
            <dd data-testid="settings-project-info-archived">
              <Badge variant={project?.isArchived ? 'destructive' : 'outline'}>
                {project?.isArchived ? tProj('status.archived') : tProj('status.active')}
              </Badge>
            </dd>
          </div>
        </dl>
      </div>

      <Separator />

      {/* ── 3. Zona de peligro ────────────────────────────────── */}
      <DangerZone
        title={t('projectInfo.deleteTitle')}
        description={t('projectInfo.deleteDescription')}
        actionTitle={t('projectInfo.deleteActionTitle')}
        actionDescription={t('projectInfo.deleteActionDescription')}
        buttonLabel={t('projectInfo.deleteButton')}
        onAction={() => setDeleteDialogOpen(true)}
        testId="settings-project-delete-zone"
        buttonTestId="settings-project-delete-button"
      />

      <ProjectDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        projectName={project?.name ?? ''}
        isPending={deleteProject.isPending}
        onConfirm={async () => {
          if (!selectedProjectId) return;
          await deleteProject.mutateAsync(selectedProjectId);
        }}
      />

      <ConfirmDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={options?.title ?? ''}
        description={options?.description}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
